import axios from 'axios'
import {
  ApiTotalsModel,
  ListingQueryModel,
  UpdateListingModel,
  UpdateListingPublicDataModel,
  PriceModel,
  ListingDiscountModel,
  LISTING_STATES,
  CreateListingModel,
} from '@loavies/c2c-models'
import { ErrorHandlerService } from '@loavies/utils'

import { mapRelations } from '../response-mappers/relations-mapper'
import {
  mapListing,
  mapListings,
  sortListingsByCreatedAt,
} from '../response-mappers/listing-mappers'
import { PAGINATION } from './constants/pagination'
import { MarketplaceImageService } from './marketplace-image-service'

export class MarketplaceListingService {
  constructor(marketplaceService) {
    this.marketplaceService = marketplaceService
    this.marketplace = marketplaceService.instance
    this.errorHandlerService = new ErrorHandlerService()
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#show-own-listing
   * @param {string} id
   * @return {Promise<loavies.models.listing.ListingModel>}
   */
  getOwnListingById(id) {
    return this.marketplace.ownListings
      .show({
        id,
        include: ['images', 'author', 'author.profileImage', 'currentStock.quantity'],
        ...this.marketplaceService.getImageVariants(),
      })
      .then(response => mapRelations(response.data))
      .then(response => mapListing(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * Because Sharetribe doesn't support query parameters for own listings
   * we need to retrieve all listings and filter on the front-end.
   * https://www.sharetribe.com/api-reference/marketplace.html#query-own-listings
   * @return {Promise<loavies.models.listing.ListingModel[]>}
   */
  async getOwnListings() {
    let totalPages
    let listings

    await this.query({
      page: 1,
      perPage: PAGINATION.MAXIMUM_PAGE_SIZE,
    })
      .then(response => {
        totalPages = response.meta.totalPages
        listings = mapListings(response.data)
      })

    if (totalPages === 1) {
      return listings
    }

    const promises = []

    // Starting at page 2
    for (let i = 2; i <= totalPages; i++) {
      promises.push(
        this.query({
          page: i,
          perPage: PAGINATION.MAXIMUM_PAGE_SIZE,
        })
        .then(response => {
          listings = listings.concat(mapListings(response.data))
        })
      )
    }

    await Promise.all(promises)

    // Because all the promises are fired at once we sort
    // the listings manually after all the promises have resolved.
    return sortListingsByCreatedAt(listings)
  }

  /**
   * @return {Promise<loavies.models.listing.ListingModel[]>}
   */
  getOwnDraftListings() {
    return this.getOwnListings()
      .then(listings => listings.filter(listing => listing.state === LISTING_STATES.DRAFT))
  }

  /**
   * @return {Promise<loavies.models.listing.ListingModel[]>}
   */
  getOwnPublishedListings() {
    return this.getOwnListings()
      .then(listings => listings.filter(listing => listing.state === LISTING_STATES.PUBLISHED))
  }

  /**
   * @param {Array} data
   * @param {Object} meta
   * @return {{
   *  listings: loavies.models.listing.ListingModel[],
   *  totals: loavies.models.api.ApiTotalsModel
   * }}
   */
  mapMultipleListingsResponse({ data, meta }) {
    return {
      listings: mapListings(data),
      totals: new ApiTotalsModel(meta),
    }
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#create-listing
   * @param {loavies.models.listing.CreateListingModel} listing
   * @return {Promise<loavies.models.listing.ListingModel>} created listing
   */
  createListing(listing) {
    return this.marketplace.ownListings
      .create(listing, {
        expand: true,
          include: ['images', 'author', 'author.profileImage', 'currentStock.quantity'],
          ...this.marketplaceService.getImageVariants(),
      })
      .then(response => mapRelations(response.data))
      .then(response => mapListing(response.data))
      .then(async listing => {
        const listingId = listing.id

        await this.marketplace.stockAdjustments.create({ listingId, quantity: 1 })

        return listing
      })
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * @param {loavies.models.listing.ListingModel} listing
   * @return {Promise<loavies.models.listing.ListingModel>} created listing
   */
  async createListingFromExistingListing(listing) {
    try {
      const copiedImageIds = await this.getCopiedImageIds(listing.images)

      const newListing = new CreateListingModel({
        title: listing.title,
        description: listing.description,
        price: listing.price,
        privateData: listing.privateData,
        publicData: listing.publicData.assign({
          timesShared: 0,
          wishlistCount: 0,
        }),
        images: copiedImageIds,
      })

      return this.createListing(newListing)
    } catch (error) {
      return Promise.reject(error)
    }
  }

  /**
   * Download the images and upload these again to Sharetribe.
   * Returns the newly created image id's
   * @param {loavies.models.image[]} images
   * @return {Promise<string[]>}
   */
  async getCopiedImageIds(images) {
    const marketplaceImageService = new MarketplaceImageService(this.marketplaceService)
    const imageBlobs = await Promise.all(images.map(image => this.getImageBlob(image.variants.original.url)))

    return Promise.all(imageBlobs.map(imageBlob => marketplaceImageService.uploadImage(imageBlob)))
  }

  /**
   * @param imageUrl
   * @return {Promise<Blob>}
   */
  getImageBlob(imageUrl) {
    return axios({
      url: imageUrl,
      method: 'GET',
      responseType: 'blob',
    })
      .then(response => response.data)
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#create-draft-listing
   * @param {loavies.models.listing.CreateListingModel} listing
   * @return {Promise<loavies.models.listing.ListingModel>} created draft listing
   */
  createDraftListing(listing) {
    return this.marketplace.ownListings
      .createDraft(listing, {
        expand: true,
        include: ['images', 'author', 'author.profileImage', 'currentStock.quantity'],
        ...this.marketplaceService.getImageVariants(),
      })
      .then(response => mapRelations(response.data))
      .then(response => mapListing(response.data))
      .then(async listing => {
        const listingId = listing.id

        await this.marketplace.stockAdjustments.create({ listingId, quantity: 1 })

        return listing
      })
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#publish-draft-listing
   * @param {string} id listingId
   * @return {Promise<loavies.models.listing.ListingModel>}
   */
  publishDraftListing(id) {
    return this.marketplace.ownListings
      .publishDraft({ id }, {
        expand: true,
        include: ['images', 'author', 'author.profileImage', 'currentStock.quantity'],
        ...this.marketplaceService.getImageVariants(),
      })
      .then(() => {
        const publishedAtDate = new Date()
        const shopIndex = publishedAtDate.getTime()

        return this.updateListing(new UpdateListingModel({
          id,
          publicData: new UpdateListingPublicDataModel({
            publishedAt: publishedAtDate.toISOString(),
            shopIndex,
          }),
        }))
      })
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#discard-draft-listing
   * @param {string} id listingId
   * @return {Promise}
   */
  discardDraftListing(id) {
    return this.marketplace.ownListings.discardDraft({ id })
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#add-image-to-listing
   * @param {string} listingId
   * @param {string} imageId
   * @return {Promise<string>} updated listing id
   */
  addImageToListing(listingId, imageId) {
    return this.marketplace.ownListings
      .addImage({ id: listingId, imageId })
      .then(response => mapRelations(response.data))
      .then(response => response.data.id.uuid)
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#open-listing
   * Republishes listing
   * @param {string} id listingId
   * @return {Promise<loavies.models.listing.ListingModel>}
   */
  openListing(id) {
    return this.marketplace.ownListings
      .open({ id }, {
        expand: true,
        include: ['images', 'author', 'author.profileImage', 'currentStock.quantity'],
        ...this.marketplaceService.getImageVariants(),
      })
      .then(response => mapRelations(response.data))
      .then(response => mapListing(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#update-listing
   * @param {loavies.models.listing.UpdateListingModel} updateListingModel
   * @return {Promise<loavies.models.listing.ListingModel>} updated listing
   */
  updateListing(updateListingModel) {
    return this.marketplace.ownListings
      .update(updateListingModel, {
        expand: true,
        include: ['images', 'author', 'author.profileImage', 'currentStock.quantity'],
        ...this.marketplaceService.getImageVariants(),
      })
      .then(response => mapRelations(response.data))
      .then(response => mapListing(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * @param {string} id
   * @param {number} amount
   * @returns {Promise<loavies.models.listing.ListingModel>}
   */
  updatePrice({
    id,
    amount,
  }) {
    return this.updateListing(new UpdateListingModel({
      id,
      price: new PriceModel({
        amount,
      }),
    }))
  }

  /**
   * @param {string} id
   * @param {string} condition
   * @returns {Promise<loavies.models.listing.ListingModel>}
   */
  updateCondition({
    id,
    condition,
  }) {
    return this.updateListing(new UpdateListingModel({
      id,
      publicData: new UpdateListingPublicDataModel({
        condition,
      }),
    }))
  }

  /**
   * @param {string} id
   * @param {string} description
   * @returns {Promise<loavies.models.listing.ListingModel>}
   */
  updateDescription({
    id,
    description,
  }) {
    return this.updateListing(new UpdateListingModel({
      id,
      description,
    }))
  }

  /**
   * @param {string} id
   * @param {string|null} labelId
   * @return {Promise<loavies.models.listing.ListingModel>} updated listing
   */
  updateLabel({
    id,
    labelId,
  }) {
    return this.updateListing(new UpdateListingModel({
      id,
      publicData: new UpdateListingPublicDataModel({
        labelId,
      }),
    }))
  }

  /**
   * @param {string} id
   * @param {loavies.models.image.ImageModel[]|string[]} images
   * @returns {Promise<loavies.models.listing.ListingModel>}
   */
  updateImages({
    id,
    images,
  }) {
    return this.updateListing(new UpdateListingModel({
      id,
      images,
    }))
  }

  /**
   * @param {string} id
   * @param {loavies.models.base.PriceModel} originalPrice
   * @param {number} [discountPercentage]
   * @param {loavies.models.base.PriceModel} [newPrice]
   * @return {Promise<loavies.models.listing.ListingModel>} updated listing
   */
  updateDiscount({
    id,
    originalPrice,
    discountPercentage,
    newPrice,
  }) {
    // Remove discount from public data if nog discount is given.
    if (!discountPercentage || !newPrice) {
      return this.updateListing(new UpdateListingModel({
        id,
        price: new PriceModel({
          amount: originalPrice.amount,
          currency: originalPrice.currency,
        }),
        publicData: new UpdateListingPublicDataModel({
          discount: null,
        }),
      }))
    }

    return this.updateListing(new UpdateListingModel({
      id,
      price: new PriceModel({
        amount: newPrice.amount,
        currency: newPrice.currency,
      }),
      publicData: new UpdateListingPublicDataModel({
        discount: new ListingDiscountModel({
          discountPercentage,
          originalPrice: new PriceModel({
            amount: originalPrice.amount,
            currency: originalPrice.currency,
          }),
        }),
      }),
    }))
  }

  /**
   * @param {string} id
   * @param {number} shopIndex
   * @returns {Promise<loavies.models.listing.ListingModel>}
   */
  updateShopIndex({
    id,
    shopIndex,
  }) {
    return this.updateListing(new UpdateListingModel({
      id,
      publicData: new UpdateListingPublicDataModel({
        shopIndex,
      }),
    }))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#close-listing
   * Un-publish listing
   * @param {string} id listingId
   * @return {Promise<loavies.models.listing.ListingModel>}
   */
  closeListing(id) {
    return this.marketplace.ownListings
      .close({ id }, {
        expand: true,
        include: ['images', 'author', 'author.profileImage', 'currentStock.quantity'],
        ...this.marketplaceService.getImageVariants(),
      })
      .then(response => mapRelations(response.data))
      .then(response => mapListing(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }

  /**
   * https://www.sharetribe.com/api-reference/marketplace.html#query-listings
   * @param {loavies.models.listing.ListingQueryModel} [query]
   * @param {number} [page]
   * @param {number} [perPage]
   * @return {Promise<{
   *  data: Array,
   *  meta: Object,
   * }>}
   */
  async query({
    query = new ListingQueryModel({}),
    page = PAGINATION.DEFAULT_PAGE,
    perPage = PAGINATION.DEFAULT_ITEMS_PER_PAGE,
  } = {}) {
    return this.marketplace.ownListings
      .query({
        ...query,
        include: ['images', 'author', 'author.profileImage', 'currentStock.quantity'],
        ...this.marketplaceService.getImageVariants(),
        page,
        perPage,
      })
      .then(response => mapRelations(response.data))
      .catch(response => Promise.reject(this.errorHandlerService.mapMarketplaceErrors(response.data.errors)))
  }
}
